import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";

/**
 * Updates the cookies for refresh and access tokens, and sets the expiry time for the refresh token cookie. Sets the allowed sources cookie.
 * @param authResponse - Response from the authentication / refresh request.
 */
export function cacheTokensWithExpiration(authResponse){
  try{
    const { iat, exp } = jwtDecode(authResponse.data.shortTermAccessToken);
    const timeDifferenceMinutes = (exp - iat) / 60;
    // Set refresh token in local storage
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("allowedSources");
    localStorage.setItem("refreshToken", authResponse.data.longTermRefreshToken.toString());
    localStorage.setItem("allowedSources", authResponse.data.allowedSources);

    // Set access token in cookies
    // https://github.com/js-cookie/js-cookie/tree/latest#readme
    Cookies.remove("accessToken");
    Cookies.set("accessToken", authResponse.data.shortTermAccessToken.toString(), {
      expires: minutesToDays(timeDifferenceMinutes),
      secure: true
    });
  } catch(e){
    console.error("Error while caching tokens with expiration: " + e);
  }
}

/**
 * Converts minutes to days.
 * @param {number} minutes - Number of minutes to convert.
 * @returns {number} Equivalent number of days.
 */
export function minutesToDays(minutes) {
  return minutes / 1440;
}
export class ReviewStateEnum {
  static Pending = 1;
  static Approved = 2;
  static Rejected = 3;
}
let dateFormat = {
  year: "2-digit",
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * Converts a date to a readable format.
 * @param {Date} date - Date to convert.
 * @returns {string} Date in a readable format.
 */
export function ReadableLocaleDate(date) {
  // Verify that the date is valid
  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) {
    return "N/A";
  }
  return parsedDate.toLocaleString("en-US", dateFormat).replace(",", "");
}

let currencyFormat = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * Converts a number to a currency format.
 * @param {number} amount - Amount to convert.
 * @returns {string} Amount in currency format.
 */
export function ToCurrency(amount) {
  return new Intl.NumberFormat("en-US", currencyFormat).format(amount);
}

/**
 * Converts HTML to plain text.
 * @param {string} html - HTML string to convert.
 * @returns {string} Plain text.
 */
export function ConvertToText(html) {
  let text = document.createElement("textarea");
  text.innerHTML = html;
  return text.value;
}

/**
 * Validates an email address.
 * @param {string} email - Email address to validate.
 * @returns {boolean} True if valid, false otherwise.
  */
export function validateEmail(email) {
  const emailRegex = /\S+@\S+\.\S+/;
  return emailRegex.test(email);
}

/**
 * getCurrentDate - Returns the current date in the format of "Month Day, Year".
 * @returns {string} Current date in the format of "Month Day, Year".
 */
export function getCurrentDate() {
  let date = new Date();
  let month = date.toLocaleString("default", { month: "long" });
  let day = date.getDate();
  let year = date.getFullYear();

  return `${month} ${day}, ${year}`;
}

/**
 * getCurrentTime - Returns the current time in the format of "Hours:Minutes AM/PM".
 * @returns {string} Current time in the format of "Hours:Minutes AM/PM".
 */
export function getCurrentTime() {
  let time = new Date();
  return time.toLocaleString("en-US", { hour: 'numeric', minute: 'numeric', hour12: true });
}


/**
 * convertMicrEdit - Returns an editable string with MICR characters converted to their editable counterparts.
 * @param {*} input
 */
export function convertMicrEdit(input) {
  if (input === null || input === undefined) {
    return "";
  }
  
  return input.replaceAll("⑆", ":")
      .replaceAll("⑇", '"')
      .replaceAll("⑈", "<")
      .replaceAll("⑉", "-");
}

/**
 * convertMicrView - Returns a viewable string with editable characters converted to their MICR counterparts.
 * @param {*} input 
 */
export function convertMicrView(input) {
  if (input === null || input === undefined) {
    return "";
  }
  
  return input.replaceAll(":", "⑆")
      .replaceAll('"', "⑇")
      .replaceAll("<", "⑈")
      .replaceAll("-", "⑉")
      .replaceAll("=", "⑉");
}

/**
 * @description Handles errors from API calls.
 * @param error - Error response from the API call.
 * @returns {*} - A parsed error response.
 */
export function onCallError(error) {
  return error.response;
}
